import stringify from 'json-stable-stringify';
import React, {useEffect, useRef, useState} from 'react';
import {debounce} from 'ts-debounce';
import {v4 as uuidv4} from 'uuid';

import notifications from 'notifications';
import {
  MIN_QUERY_LENGTH,
  SEARCH_CORRELATION_ID_HEADER,
  SearchContext,
  SHORT_QUERY_MESSAGE,
  Filter,
  Sort,
  REFINE_INPUT_THRESHOLD,
  REFINE_INPUT_MESSAGE,
} from 'search/SearchConstants';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import {defaultRenderOptionContent} from 'ui-library/menus/MenuUtil';
import * as net from 'util/Net';

import ChipSelectInput, {ChipSelectInputProps} from './ChipSelectInput';

export const DEBOUNCE_MS = 250;

const SHORT_QUERY_SENTINEL: OptionData = {name: SHORT_QUERY_MESSAGE, value: {}, disabled: true};
const REFINE_INPUT_SENTINEL: OptionData = {name: REFINE_INPUT_MESSAGE, value: {}, disabled: true};
const NO_RESULTS_SENTINEL = {};
// TODO(BNCH-17577): Most searches (by # of components that trigger it) are for write actions, so we default
// to that for now but we should instrument them explicitly.
const DEFAULT_SEARCH_CONTEXT = SearchContext.FOR_WRITE_ACTION;

const doPostSearch: ChipSearchInputSearch = (
  url: string,
  query: string,
  params: Object,
  callbacks: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    done: (resp: any, status: any, xhr: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fail: (resp: any) => void;
    always: () => void;
  }
): void => {
  net
    .postJSON({url, data: {query, ...params}})
    .done(callbacks.done)
    .fail(callbacks.fail)
    .always(callbacks.always);
};

/**
 * Search items debounced. Intended for use with endpoints that use SEARCH_PARAMS_SPEC with Sightglass2.
 */
export const searchPost = debounce(doPostSearch, DEBOUNCE_MS);

const doGetSearch: ChipSearchInputSearch = (
  url: string,
  query: string,
  params: Object,
  callbacks: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    done: (resp: any, status: any, xhr: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fail: (resp: any) => void;
    always: () => void;
  }
): void => {
  net
    .getJSON(url, {q: query, ...params})
    .done(callbacks.done)
    .fail(callbacks.fail)
    .always(callbacks.always);
};

/**
 * Search items debounced. Deprecated and only kept for back-compatibility from before the SG2 overhaul.
 * Prefer doPostSearch instead.
 */
export const deprecatedSearchGet = debounce(doGetSearch, DEBOUNCE_MS);

export type ChipSearchInputSearch = (
  url: string,
  query: string,
  params: Object,
  callbacks: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    done: (resp: any, status: any, xhr: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fail: (resp: any) => void;
    always: () => void;
  }
) => void;

export interface NoResultOption {
  option: OptionData;
  onClick: () => void;
}

export interface ChipSearchInputProps<TSearchResponse = unknown>
  extends Omit<ChipSelectInputProps, 'optionDatas'> {
  emptyQueryMessage?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOptionDatasFromResponse: (response: any) => Array<OptionData>;
  getCorrelationId?: (response: TSearchResponse) => string | null;
  searchURL?: string;
  fetchParams: {
    query?: string;
    limit?: number;
    offset?: number;
    filters?: Array<Filter>;
    sorts?: Array<Sort>;
    context?: SearchContext;
  };
  doSearch?: ChipSearchInputSearch;
  // We are switching to doSearchPromise as part of the Consolidated Dropdown work, and we'll be
  // removing doSearch and making doSearchPromise required once dropdown components are switched over
  // to using SearchInput instead of ChipSearchInput directly.
  // SearchURL will also be removed, and fetchParams will be simplified to just limit.
  doSearchPromise?: () => Promise<Promise<TSearchResponse>>;
  searchOnEmptyQuery: boolean;
  renderOptionContent?: (optionData: OptionData) => JSX.Element;
  noResultsOptions?: Array<NoResultOption> | null;
  onSearchStart?: () => void;
  onSearchSuccess?: (resp: TSearchResponse) => void;
}

function getNoResultsOptionDatas(
  errorMessage: string | null,
  isSearching: boolean,
  query?: string,
  noResultsOptions?: Array<NoResultOption> | null
): Array<OptionData> {
  let display;
  let shouldShowNoResultsOption = false;
  if (errorMessage != null) {
    display = errorMessage;
  } else if (isSearching) {
    display = <HorizontalThrobber />;
  } else if (!query) {
    display = 'Search for an item.';
    shouldShowNoResultsOption = noResultsOptions != null && noResultsOptions.length > 0;
  } else if (query.length > 0 && query.length < MIN_QUERY_LENGTH) {
    display = SHORT_QUERY_MESSAGE;
  } else {
    display = 'No results found.';
  }
  const displayData: Array<OptionData> = [
    {
      name: display,
      className: shouldShowNoResultsOption ? '' : 'u-text-align-center',
      skipIconOverride: true,
    },
  ];
  return shouldShowNoResultsOption
    ? [
        ...displayData,
        ...noResultsOptions!.map(({option, onClick}) => ({
          ...option,
          value: {
            // This `value` object is passed to our internal click handler below. We set it up so
            // that we know when we should intercept & handle it. This is used in this file only.
            _sentinel: NO_RESULTS_SENTINEL,
            onClick,
          },
        })),
      ]
    : displayData;
}

export default function ChipSearchInput<TSearchResponse>(
  props: ChipSearchInputProps<TSearchResponse>
): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [optionDatas, setOptionDatas] = useState<Array<OptionData>>([]);
  const mostRecentSearchIdRef = useRef<string | null>(null);

  const {
    query,
    fetchParams,
    searchURL,
    getOptionDatasFromResponse,
    getCorrelationId,
    doSearch,
    doSearchPromise,
    searchOnEmptyQuery,
    renderOptionContent,
    onSearchStart,
    onSearchSuccess,
  } = props;

  useEffect(
    () => {
      // Lets us detect if the effect was cleaned up by the time we try to do the search call.
      // See https://github.com/facebook/react/issues/14369#issuecomment-468267798
      let isUnmounted = false;

      // If the component is unmounted, or the search that is completing is not the most recent search,
      // we consider the search stale and do not render its results.
      const isStaleSearch = (searchId: string): boolean =>
        isUnmounted || mostRecentSearchIdRef.current !== searchId;
      const search = async (): Promise<void> => {
        if (!query && !searchOnEmptyQuery) {
          return;
        }

        const searchId = uuidv4();
        mostRecentSearchIdRef.current = searchId;

        const params = {...fetchParams, context: fetchParams.context || DEFAULT_SEARCH_CONTEXT};

        onSearchStart?.();
        if (doSearchPromise) {
          // This is the new way to do searches, will remove the old way once all components are updated.
          try {
            const resp = await doSearchPromise();

            onSearchSuccess?.(resp);
            if (isStaleSearch(searchId)) {
              return;
            }
            let newOptionDatas = getOptionDatasFromResponse(resp);

            if (query && query.length > 0 && query.length < MIN_QUERY_LENGTH) {
              newOptionDatas = [SHORT_QUERY_SENTINEL, ...newOptionDatas];
            } else if (
              params.limit
                ? newOptionDatas.length === params.limit
                : newOptionDatas.length >= REFINE_INPUT_THRESHOLD
            ) {
              newOptionDatas = [REFINE_INPUT_SENTINEL, ...newOptionDatas];
            }

            // Add the required attributes to the item for search click tracking. The click tracking is not
            // handled in this component because the shape of the OptionDatas is not known to extract the
            // item id.
            const searchCorrelationId = getCorrelationId?.(resp) ?? null;
            newOptionDatas.forEach((optionData, index) => {
              // Value is optional and of any type, so only populate the field if the value exists and is an
              // object. Since typeof null returns object, also confirm it isn't null.
              if (typeof optionData.value === 'object' && optionData.value != null) {
                optionData.value._searchCorrelationId = searchCorrelationId;
                optionData.value._searchPosition = index + 1;
              }
            });

            setOptionDatas(newOptionDatas);
            setErrorMessage(null);
          } catch (error) {
            if (!net.isServerRequestError(error)) {
              throw error;
            }

            if (isStaleSearch(searchId)) {
              return;
            }

            const newErrorMessage = error.message || 'An error occurred.';
            setOptionDatas([]);
            setErrorMessage(newErrorMessage);
          } finally {
            if (!isStaleSearch(searchId)) {
              setIsSearching(false);
              mostRecentSearchIdRef.current = null;
            }
          }
        } else {
          if (doSearch == null || searchURL == null) {
            throw new Error('ChipSearchInput requires either doSearchPromise or doSearch and searchURL');
          }

          // TODO: remove old callback way of handling searches once dropdowns are consolidated
          doSearch(searchURL, query!, params, {
            done: (resp, status, xhr) => {
              onSearchSuccess?.(resp);
              if (isStaleSearch(searchId)) {
                return;
              }
              let newOptionDatas = getOptionDatasFromResponse(resp);

              if (query && query.length > 0 && query.length < MIN_QUERY_LENGTH) {
                newOptionDatas = [SHORT_QUERY_SENTINEL, ...newOptionDatas];
              } else if (
                params.limit
                  ? newOptionDatas.length === params.limit
                  : newOptionDatas.length >= REFINE_INPUT_THRESHOLD
              ) {
                newOptionDatas = [REFINE_INPUT_SENTINEL, ...newOptionDatas];
              }

              // Add the required attributes to the item for search click tracking. The click tracking is not
              // handled in this component because the shape of the OptionDatas is not known to extract the
              // item id.
              const searchCorrelationId = xhr?.getResponseHeader(SEARCH_CORRELATION_ID_HEADER);
              newOptionDatas.forEach((optionData, index) => {
                // Value is optional and of any type, so only populate the field if the value exists and is an
                // object.
                if (typeof optionData.value === 'object') {
                  optionData.value._searchCorrelationId = searchCorrelationId;
                  optionData.value._searchPosition = index + 1;
                }
              });

              setOptionDatas(newOptionDatas);
              setErrorMessage(null);
            },
            fail: (resp) => {
              if (isStaleSearch(searchId)) {
                return;
              }
              const newErrorMessage = notifications.getFriendlyError(resp) || 'An error occurred.';
              setOptionDatas([]);
              setErrorMessage(newErrorMessage);
            },
            always: () => {
              if (isStaleSearch(searchId)) {
                return;
              }
              setIsSearching(false);
              mostRecentSearchIdRef.current = null;
            },
          });
        }
      };

      // Always clear the optionDatas to scrub any stale state. This is safe to do because this code only
      // executes when the query or fetchParams have changed. If we trigger another search, this will get
      // populated downstream.
      setOptionDatas([]);

      if (!query && !searchOnEmptyQuery) {
        setIsSearching(false);
        setErrorMessage(null);
        mostRecentSearchIdRef.current = null;
      } else {
        setIsSearching(true);
        setErrorMessage(null);
        search();
      }

      return () => {
        isUnmounted = true;
      };
    },
    // Don't include getOptionDatasFromResponse or doSearch as dependencies, as function props are often
    // redefined on every render and we wouldn't want to fetch on every render. If the caller wants to change
    // its getOptionDatasFromResponse function, it should force a re-render by using the `key` prop.

    // Additionally, stringify fetchParams since dependencies are checked by identity, and we only want to
    // re-search if the params actually change.
    // See https://github.com/facebook/react/issues/14476#issuecomment-471199055
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query, stringify(fetchParams), searchURL, searchOnEmptyQuery]
  );

  const renderSearchOptionContent = (optionData: OptionData): JSX.Element => {
    if (optionData === SHORT_QUERY_SENTINEL) {
      const {value, ...optionDataWithoutValue} = optionData;
      return defaultRenderOptionContent(optionDataWithoutValue);
    }
    if (optionData === REFINE_INPUT_SENTINEL) {
      const {value, ...optionDataWithoutValue} = optionData;
      return defaultRenderOptionContent(optionDataWithoutValue);
    }
    return (renderOptionContent || defaultRenderOptionContent)(optionData);
  };

  if (optionDatas.length) {
    return (
      <ChipSelectInput {...props} optionDatas={optionDatas} renderOptionContent={renderSearchOptionContent} />
    );
  }

  function onSelect(value: OptionValue): void {
    // We craft a special value above for the "no results" objects
    if (value != null && value._sentinel === NO_RESULTS_SENTINEL) {
      value.onClick();
      return;
    }
    if (props.onSelect != null) {
      props.onSelect(value);
    }
  }

  const noResultsDatas = getNoResultsOptionDatas(errorMessage, isSearching, query, props.noResultsOptions);
  const hasExtraNoResultsItems = noResultsDatas.length > 1;
  return (
    <ChipSelectInput
      {...props}
      onSelect={hasExtraNoResultsItems ? onSelect : null}
      renderOptionContent={null}
      optionDatas={noResultsDatas}
    />
  );
}
